import HealthDashboard from "@/components/home";
import Onboarding from "@/components/onboarding";
import ProfileCompletionModal from "@/components/profile-completion-modal";
import { useState, useMemo, useEffect } from "react";
import { useMe } from "@/api";
import { Loader } from "@mantine/core";
import type { User } from "@/api/types";
import { useTour } from "@reactour/tour";

export function checkProfileCompletion(userData: User | undefined | null): {
  missingFields: {
    first_name: boolean;
    last_name: boolean;
    date_of_birth: boolean;
    address: boolean;
  };
  isComplete: boolean;
} {
  if (!userData) {
    return {
      missingFields: {
        first_name: true,
        last_name: true,
        date_of_birth: true,
        address: true,
      },
      isComplete: false,
    };
  }

  const address = userData.address || {};
  const addressMissing = 
    !userData.address || 
    Object.keys(address).length === 0 ||
    !address.street_address || 
    (typeof address.street_address === 'string' && address.street_address.trim() === "") ||
    !address.city || 
    (typeof address.city === 'string' && address.city.trim() === "") ||
    !address.state || 
    (typeof address.state === 'string' && address.state.trim() === "") ||
    !address.zip_code || 
    (typeof address.zip_code === 'string' && address.zip_code.trim() === "") ||
    !address.country || 
    (typeof address.country === 'string' && address.country.trim() === "");

  const missingFields = {
    first_name: !userData.first_name || userData.first_name.trim() === "",
    last_name: !userData.last_name || userData.last_name.trim() === "",
    date_of_birth: !userData.date_of_birth,
    address: addressMissing,
  };

  const isComplete = !(
    missingFields.first_name || 
    missingFields.last_name || 
    missingFields.date_of_birth || 
    missingFields.address
  );

  return {
    missingFields,
    isComplete,
  };
}


export default function Home() {
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const { data: userData, isLoading, refetch } = useMe();
  const { setIsOpen } = useTour();

  const { missingFields, isComplete: isProfileComplete } = useMemo(() => {
    return checkProfileCompletion(userData);
  }, [userData]);

  const hasMissingFields = !isProfileComplete;

  useEffect(() => {
    if (!isLoading && userData && hasMissingFields) {
      setShowProfileModal(true);
    }
  }, [isLoading, userData, hasMissingFields]);

  useEffect(() => {
    const showOnboarding = userData?.is_onboarded === false && !hasMissingFields;
    
    if (!isLoading && userData && !hasMissingFields && !showOnboarding) {
      const skipIntro = Boolean(userData.skip_intro);
      const hasSeenWelcomeTour = Boolean((userData as any).has_seen_welcome_tour);
      
      if (!skipIntro && !hasSeenWelcomeTour) {
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 300);
        
        return () => clearTimeout(timer);
      } else {
        setIsOpen(false);
      }
    } else {
      setIsOpen(false);
    }
  }, [isLoading, userData, hasMissingFields, setIsOpen]);

  const handleSkipOnboarding = () => {
    // Refetch user data to get updated is_onboarded status
    // The is_onboarded status will be updated by the API call in OnboardingScreen
    refetch();
  };

  const handleOnboardingComplete = (_isOnboarding: boolean) => {
    // Refetch user data to get updated is_onboarded status
    // The is_onboarded status will be updated by the API call in OnboardingScreen
    refetch();
  };

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    refetch();
  };

  // Show loading state while fetching user data
  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Loader size="lg" />
      </div>
    );
  }

  // Show onboarding if user is not onboarded, otherwise show home screen
  const showOnboarding = userData?.is_onboarded === false && !hasMissingFields;

  return (
    <>
      <ProfileCompletionModal
        opened={showProfileModal}
        onComplete={handleProfileComplete}
        missingFields={missingFields}
      />
      
      {!hasMissingFields && (
        <>
          {showOnboarding ? (
            <Onboarding
              setIsOnboarding={handleOnboardingComplete}
              onSkip={handleSkipOnboarding}
            />
          ) : (
            <HealthDashboard />
          )}
        </>
      )}
    </>
  );
}
